import flatten from 'lodash/flatten';
import AwesomeDebouncePromise from 'awesome-debounce-promise';

import { PlanCreateParams, PlanData, PlanListResponse, PlanQuery, PlanType } from 'chaika-frontend-types/frontend-cms';

import { PlanForm } from '@plan/store/PlanForm';
import { serviceService } from '@services/services/serviceService';
import { serviceGroupService } from '@services/services/serviceGroupService';
import { store } from '@store';
import { PlanApi } from '@api/PlanApi';
import { PlanVersionApi } from '@api/PlanVersionApi';
import { getUniqNewIds } from '@utils/getUniqNewIds';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';
import { contractService } from '@organization/service/contract/contractService';
import { LegalEntityType } from 'chaika-enums';

const findApi = AwesomeDebouncePromise(PlanVersionApi.getList, 500);

class PlanService {
    private onLoadNext = false;

    public async loadByIds(ids: string[], actualOn?: string): Promise<void> {
        const planList = store.planStore.planList;

        const newIds = getUniqNewIds(planList.ids, ids);

        if (newIds.length > 0) {
            const chunkIds = getUniqChunkedIds(newIds);

            const [...planParams] = await Promise.all(
                chunkIds.map(ids => PlanApi.getList({ actualOn, ids: ids.join(',') })),
            );

            let newCount = 0;

            planParams.forEach(({ plans, count }) => {
                planList.add(plans);
                newCount = newCount + count;
            });

            planList.count = planList.count + newCount;
        }
    }

    public async find(query: PlanQuery): Promise<PlanListResponse> {
        return PlanApi.getList(query);
    }

    public async get(id: string): Promise<PlanData> {
        const planList = store.planStore.planList;
        let plan = await planList.get(id);

        if (!plan) {
            plan = await PlanApi.getPlan(id);
            planList.add([plan]);
            planList.count = planList.count + 1;
        }

        return plan;
    }

    public async updatePlan(): Promise<void> {
        const {
            planStore,
            planStore: { planForm },
        } = store;

        await PlanApi.update(planForm.id, planForm.updateParams);
        planStore.planList.update(planForm.id, planForm.serialize());
    }

    public async createPlan(params?: PlanCreateParams): Promise<string> {
        const planStore = store.planStore;
        const createParams = params ?? planStore.planForm.createParams;
        const planId = params?.id ?? planStore.planForm.id;

        await PlanApi.create(createParams);

        const plan = await PlanApi.getPlan(planId);

        planStore.planForm = new PlanForm(plan);

        return planId;
    }

    public async loadNextVersions(stopIndex = 0): Promise<void> {
        const { planVersionList, searchList } = store.planStore;

        const list = searchList.searchParamsSet ? searchList : planVersionList;

        if (!this.onLoadNext && list.needLoadNext(stopIndex)) {
            this.onLoadNext = true;
            const { plans } = await PlanVersionApi.getList(list.queryParams);
            await this.loadRelations(flatten(plans.map(plan => plan.versions)));
            list.addNext(plans as any);
            this.onLoadNext = false;
        }
    }

    public async loadRelations(plans: PlanData[]): Promise<void> {
        let servicesIds: string[] = [];
        let serviceGroupIds: string[] = [];
        const legalEntitiesIds: string[] = [];

        plans.forEach(({ excludeServices, includeServices, excludeGroups, includeGroups, payerId, payerType }) => {
            servicesIds = servicesIds.concat([...excludeServices, ...includeServices.map(service => service.id)]);
            serviceGroupIds = serviceGroupIds.concat([...excludeGroups, ...includeGroups.map(group => group.id)]);
            if (payerId) {
                legalEntitiesIds.push(payerId);
            }
        });

        const chunkedIds = getUniqChunkedIds(legalEntitiesIds);

        await Promise.all([serviceService.loadByIds(servicesIds), serviceGroupService.loadByIds(serviceGroupIds)]);
        await Promise.all(chunkedIds.map(ids => contractService.loadLegalEntityContracts(ids)));
    }

    public async updateSearchParams(params: PlanQuery): Promise<void> {
        const searchList = store.planStore.searchList;
        searchList.setFilterParams({ ...params, offset: 0 });

        const { plans, count } = await findApi(searchList.searchParams);

        searchList.setResult(plans as any[], count);
    }

    public isAttachmentPlan(payerType: LegalEntityType | undefined): boolean {
        const availableOrganizationTypes = new Set([LegalEntityType.InsuranceCompany, LegalEntityType.Assistance]);
        return !!payerType && availableOrganizationTypes.has(payerType);
    }

    public isDirectAttachmentPlan(planType: PlanType, payerType: LegalEntityType | undefined): boolean {
        return planType !== PlanType.GuarantyLetter && this.isAttachmentPlan(payerType);
    }

    public isDiscountGuaranty(type: PlanType): boolean {
        return (
            type === PlanType.GuarantyLetter ||
            type === PlanType.GuarantyLetterPlus ||
            type === PlanType.WaitForGuarantyLetter
        );
    }

    public isDiscountProgram(type: PlanType): boolean {
        return type === PlanType.Insurance || type === PlanType.Organization || type === PlanType.DoctorSubscription;
    }

    public isDiscountPatient(type: PlanType): boolean {
        return type === PlanType.Patient;
    }

    public isDiscountDoctor(type: PlanType): boolean {
        return type === PlanType.Doctor;
    }
}

export const planService = new PlanService();
